import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { resolveAccountStatus, ownerMustVerifyEmail, ACCOUNT_STATUS } from '../utils/accountStatus';

// ─────────────────────────────────────────────────────────────────────────────
// WHERE DOES THIS SESSION BELONG RIGHT NOW — asked once, answered in one place.
//
// WHY THIS EXISTS. Four routes each decided this for themselves: RootRoute,
// LoginRoute, OnboardingRoute and ProtectedRoute all re-derived "signed out /
// needs onboarding / into the app" from raw context state, with four separately
// written ideas of when that state was settled enough to act on. They agreed on
// the happy path and diverged everywhere else — RootRoute sent anyone with a
// companyId to /dashboard without ever reading onboardingComplete, so a company
// still mid-setup took a redirect through the app to get bounced back, and any
// future gate added to one gate had to be remembered in three others.
//
// A routing decision made from state that has not settled is the bug this whole
// phase is about. So PENDING is a first-class answer here, not the absence of
// one: every caller renders the loading screen for it and navigates nowhere.
// The rule is that a destination is only ever returned when the state behind it
// is known — never as an optimistic guess that a later render corrects.
//
// The ORDER of the checks is the contract:
//   1. auth still resolving          → pending
//   2. no user                       → login
//   3. user but no profile yet       → pending   (never "no company")
//   4. profile has no companyId      → onboarding
//   5. company still loading         → pending   (never "not onboarded")
//   6. owner of a gated company whose email is unconfirmed → verify-email
//   7. company not yet let in (pending / declined)         → account-review
//   8. company.onboardingComplete    → onboarding | app
//
// Steps 3 and 5 are the two that were missing somewhere. A profile that has
// not arrived is not a profile without a company, and a company that has not
// loaded is not a company that failed onboarding.
//
// Steps 6 and 7 are THE ACCOUNT GATE (2026-10-17), and they sit HERE — not in a
// page, not beside a route — because this is where a new gate on authenticated
// routes belongs (BONEFLOW_VISION Part 2, "One Question, One Answer"). Both
// read only the company document and the token, so a grandfathered company
// (no accountStatus) and its owner skip both exactly as before. This decides
// which SCREEN a person sees; the server decides what they may DO.
// ─────────────────────────────────────────────────────────────────────────────

export const DESTINATION = {
  PENDING: 'pending',
  LOGIN: 'login',
  ONBOARDING: 'onboarding',
  VERIFY_EMAIL: 'verifyEmail',
  ACCOUNT_REVIEW: 'accountReview',
  APP: 'app',
};

// Where each gate destination lives, for the routes that redirect to it.
export const DESTINATION_PATH = {
  [DESTINATION.LOGIN]: '/login',
  [DESTINATION.ONBOARDING]: '/onboarding',
  [DESTINATION.VERIFY_EMAIL]: '/verify-email',
  [DESTINATION.ACCOUNT_REVIEW]: '/account-review',
  [DESTINATION.APP]: '/dashboard',
};

/**
 * Pure resolver — no hooks, no React, so it can be reasoned about (and tested)
 * on its own. Takes exactly the values the decision depends on.
 */
export function resolveDestination({ authLoading, user, userProfile, company, companyLoading, emailVerified }) {
  if (authLoading) return DESTINATION.PENDING;
  if (!user) return DESTINATION.LOGIN;
  // Signed in, profile not here yet. NOT "this user has no company" — that
  // conflation is exactly what dropped onboarded users into onboarding.
  if (!userProfile) return DESTINATION.PENDING;
  if (!userProfile.companyId) return DESTINATION.ONBOARDING;
  // Has a company, but we have not read it yet, so onboardingComplete is
  // unknown. Unknown is pending, not false.
  if (companyLoading || !company) return DESTINATION.PENDING;
  if (ownerMustVerifyEmail({ company, user, emailVerified })) return DESTINATION.VERIFY_EMAIL;
  if (resolveAccountStatus(company) !== ACCOUNT_STATUS.ACTIVE) return DESTINATION.ACCOUNT_REVIEW;
  if (company.onboardingComplete === false) return DESTINATION.ONBOARDING;
  return DESTINATION.APP;
}

/** The same answer, wired to the live contexts. */
export function useAuthDestination() {
  const { user, userProfile, loading: authLoading, emailVerified } = useAuth();
  const { company, loading: companyLoading } = useCompany();
  return resolveDestination({ authLoading, user, userProfile, company, companyLoading, emailVerified });
}
